import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useAppSelector } from '@config/hooks';
import {
  FileManagementIcon,
  LayoutDiagramIcon,
  MasterDataIcon,
  PurposeIcon,
  SpreadsheetIcon,
  StoreIcon,
  TemperatureReportIcon,
  UserListIcon,
  CompanyIcon,
} from '@constants/svgs';
import { EUserAccessRoles, EUserRoles } from '@models/user';
import { getAccessRoleFromUserRole } from '@utils/helpers';
import { Layout, MenuProps } from 'antd';
import { MenuInfo } from 'rc-menu/lib/interface';
import { PropsWithChildren, createContext, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CollapseButtonStyled,
  CollapseButtonWrapperStyled,
  ContentStyled,
  LayoutAdminWrapperStyled,
  MenuStyled,
  SidebarStyled,
  CollapseHeaderStyled,
} from './LayoutAdminStyled';
import { TEXT_CONTENT } from './TextContent';
import { Header } from './components';

const buildVersion = `${process.env.REACT_APP_BUILD_ENV}_${process.env.REACT_APP_BUILD_DATE}_${process.env.REACT_APP_BUILD_VERSION}`;

// ----- CONTEXT -----
type TAppContext = {
  collapsed: boolean;
  role: EUserRoles[] | undefined;
  companyCode?: string | null;
  companyName?: string | null;
  storeCode?: string | null;
  storeName?: string | null;
  username?: string;
};

export const AppContext = createContext<TAppContext>({
  collapsed: false,
  role: undefined,
  companyCode: null,
  companyName: null,
  storeCode: null,
  storeName: null,
});
// ----- CONTEXT -----

type MenuItem = Required<MenuProps>['items'][number];
type TMenuItem = {
  title: string;
  path: string;
  key: string;
  icon?: JSX.Element;
  children?: TMenuItem[];
  onClick?: (e: React.MouseEvent<HTMLAnchorElement> | undefined) => void;
  afterClick?: () => void;
  accessRoles?: EUserAccessRoles[];
};

const {
  FIXED_ASSET_LEDGER,
  MANAGEMENT_SEAL_REQUEST,
  PROGRESS_REVIEW,
  NX_INTEGRATION,
  MASTER_DATA,
  USER_MASTER_LIST,
  COMPANY_MASTER_LIST,
  DISTRICT_MASTER_LIST,
  STORE_MASTER_LIST,
} = TEXT_CONTENT.SIDE_MENU;

const navFunctionCoreItems: TMenuItem[] = [
  {
    title: FIXED_ASSET_LEDGER,
    path: '/fixedassetledger',
    key: '/fixedassetledger',
    icon: (
      <span>
        <SpreadsheetIcon />
      </span>
    ),
    accessRoles: [
      EUserAccessRoles.ADMIN,
      EUserAccessRoles.STORE_OWNER,
      EUserAccessRoles.AREA_OWNER,
    ],
  },
  {
    title: MANAGEMENT_SEAL_REQUEST,
    path: '/managementsealrequest',
    key: '/managementsealrequest',
    icon: (
      <span>
        <FileManagementIcon />
      </span>
    ),
    accessRoles: [EUserAccessRoles.ADMIN, EUserAccessRoles.AREA_OWNER],
  },

  {
    title: PROGRESS_REVIEW,
    path: '/progressreview',
    key: '/progressreview',
    icon: (
      <span>
        <TemperatureReportIcon />
      </span>
    ),
    accessRoles: [EUserAccessRoles.ADMIN, EUserAccessRoles.AREA_OWNER],
  },
  {
    title: NX_INTEGRATION,
    path: '/nxintegration',
    key: '/nxintegration',
    icon: (
      <span>
        <LayoutDiagramIcon />
      </span>
    ),
    accessRoles: [EUserAccessRoles.ADMIN],
  },
];

const navMasterManagementItems: TMenuItem[] = [
  {
    title: MASTER_DATA,
    path: '/masterdata',
    key: '/masterdata',
    icon: (
      <span>
        <MasterDataIcon />
      </span>
    ),
    accessRoles: [EUserAccessRoles.ADMIN],
  },
  {
    title: USER_MASTER_LIST,
    path: '/usermasterlist',
    key: '/usermasterlist',
    icon: (
      <span>
        <UserListIcon />
      </span>
    ),
    accessRoles: [EUserAccessRoles.ADMIN],
  },
  {
    title: COMPANY_MASTER_LIST,
    path: '/companymasterlist',
    key: '/companymasterlist',
    icon: (
      <span>
        <CompanyIcon />
      </span>
    ),
    accessRoles: [EUserAccessRoles.ADMIN],
  },
  {
    title: DISTRICT_MASTER_LIST,
    path: '/districtmasterlist',
    key: '/districtmasterlist',
    icon: (
      <span>
        <PurposeIcon />
      </span>
    ),
    accessRoles: [EUserAccessRoles.ADMIN],
  },
  {
    title: STORE_MASTER_LIST,
    path: '/storemasterlist',
    key: '/storemasterlist',
    icon: (
      <span>
        <StoreIcon />
      </span>
    ),
    accessRoles: [EUserAccessRoles.ADMIN],
  },
];

const getNavbarItems = (
  items: TMenuItem[],
  userRole?: EUserAccessRoles
): MenuItem[] => {
  if (!userRole) {
    return [];
  }
  const result = items.reduce((itemAccessible: MenuItem[], item) => {
    if (!item.accessRoles || item.accessRoles?.includes(userRole)) {
      const handleClick = (e: any) => {
        item.onClick?.(e);
        item.afterClick?.();
      };
      const navItem: MenuItem = {
        label: (
          <Link to={item.path} onClick={(e) => handleClick(e)}>
            {item.title}
          </Link>
        ),
        key: item.key,
        icon: item.icon,
      };
      return [...itemAccessible, navItem];
    }
    return itemAccessible;
  }, []);
  return result;
};

const LayoutAdmin = (props: PropsWithChildren) => {
  const { children } = props;
  const [collapsed, setCollapsed] = useState(true);
  const location = useLocation();

  const {
    role: userRole,
    companyCode,
    companyName,
    storeCode,
    storeName,
    username,
  } = useAppSelector((state) => state.auth.currentUser) || {};

  const roles: EUserRoles[] =
    userRole?.map((r) => r?.roleCode as EUserRoles) ?? [];
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const handleChangeMenu = (info: MenuInfo) => {
    const [currentOpenKey] = openKeys;
    if (info.keyPath.indexOf(currentOpenKey) === -1) {
      setOpenKeys([]);
    }
  };

  const triggerCollapse = () => {
    setCollapsed((prevState) => !prevState);
  };

  const userAccessRole = getAccessRoleFromUserRole(roles);
  const sidebarCoreFunctionItems = getNavbarItems(
    navFunctionCoreItems,
    userAccessRole
  );
  const sidebarMasterManagementItems = getNavbarItems(
    navMasterManagementItems,
    userAccessRole
  );

  const selectedMenuKey = useMemo(() => {
    const allPaths = [...navFunctionCoreItems, ...navMasterManagementItems].map(
      (item) => item.path
    );
    // Nested screens (e.g. /fixedassetledger/report) share the parent menu's path prefix,
    // so match the longest path that is a prefix of the current pathname.
    const matched = allPaths
      .filter(
        (path) =>
          location.pathname === path || location.pathname.startsWith(`${path}/`)
      )
      .sort((a, b) => b.length - a.length)[0];
    return matched ?? location.pathname;
  }, [location.pathname]);

  const collapsedText = useMemo(() => {
    if (collapsed) {
      return 'collapsed';
    }
    return 'expand';
  }, [collapsed]);

  return (
    <AppContext.Provider
      value={{
        collapsed,
        role: roles,
        companyCode,
        companyName,
        storeCode,
        storeName,
        username,
      }}
    >
      <LayoutAdminWrapperStyled>
        <SidebarStyled
          collapse={collapsedText} // styled custom prop
          collapsed={collapsed} // default prop of antd
        >
          {!collapsed && (
            <CollapseHeaderStyled>業務メニュー</CollapseHeaderStyled>
          )}

          <MenuStyled
            defaultSelectedKeys={[selectedMenuKey]}
            selectedKeys={[selectedMenuKey]}
            mode="inline"
            hidden={false}
            items={sidebarCoreFunctionItems}
            openKeys={openKeys}
            onSelect={handleChangeMenu}
            collapse={collapsedText}
          />

          {!collapsed && (
            <CollapseHeaderStyled>マスタ管理</CollapseHeaderStyled>
          )}

          <MenuStyled
            defaultSelectedKeys={[selectedMenuKey]}
            selectedKeys={[selectedMenuKey]}
            mode="inline"
            hidden={false}
            items={sidebarMasterManagementItems}
            openKeys={openKeys}
            onSelect={handleChangeMenu}
            collapse={collapsedText}
          />

          <CollapseButtonWrapperStyled>
            {!collapsed && <div className="ver-build">{buildVersion}</div>}
            <CollapseButtonStyled size="large" onClick={triggerCollapse}>
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </CollapseButtonStyled>
          </CollapseButtonWrapperStyled>
        </SidebarStyled>

        <Layout>
          <Header />
          <ContentStyled collapse={collapsedText}>{children}</ContentStyled>
        </Layout>
      </LayoutAdminWrapperStyled>
    </AppContext.Provider>
  );
};

export { LayoutAdmin };
